import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ShoppingBag,
  Search,
  Filter,
  Grid,
  List,
  ArrowRight,
  Star,
  ShoppingCart,
  X,
  Store,
  MapPin,
  Phone,
  Mail,
  Globe,
  ChevronDown,
  Plus,
  Minus,
  Trash2,
  Lock,
  CreditCard,
  Truck
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const MerchantStorePage = () => {
  const { merchantId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Store data
  const [merchant, setMerchant] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View state
  const [showProducts, setShowProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  
  // Cart state - with localStorage persistence
  const [cart, setCart] = useState(() => {
    // Initialize cart from localStorage
    try {
      const savedCart = localStorage.getItem(`cart_${merchantId}`);
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  const [showCart, setShowCart] = useState(false);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [wantsDelivery, setWantsDelivery] = useState(false);
  const [checkoutDetails, setCheckoutDetails] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postcode: ""
  });

  // Prefill checkout details from user
  useEffect(() => {
    if (user) {
      setCheckoutDetails(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || ""
      }));
    }
  }, [user]);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    if (merchantId) {
      localStorage.setItem(`cart_${merchantId}`, JSON.stringify(cart));
    }
  }, [cart, merchantId]);

  useEffect(() => {
    fetchMerchantStore();
  }, [merchantId]);

  const fetchMerchantStore = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/public/merchants/${merchantId}`);
      
      if (!response.ok) {
        throw new Error("Store not found");
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setMerchant(data.merchant);
      setProducts(data.products || []);
      setCategories(data.categories || []);
    } catch (err) {
      console.error("Error fetching merchant store:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (product.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleProductClick = (productId) => {
    const product = products.find(p => p.product_id === productId);
    if (product) {
      setSelectedProduct(product);
      setShowProductDetail(true);
    }
  };

  // Add to cart function
  const addToCart = (product, e) => {
    if (e) e.stopPropagation();
    
    const isNewItemSubscription = product.type === "subscription" || product.is_subscription;
    
    // Check if cart already has items of the opposite type
    if (cart.length > 0) {
      const cartHasSubscription = cart.some(item => item.type === "subscription" || item.is_subscription);
      const cartHasOneTime = cart.some(item => !item.is_subscription && item.type !== "subscription");
      
      if (isNewItemSubscription && cartHasOneTime) {
        toast.error("Subscription and one-time products cannot be purchased together. Please complete your current cart first, then purchase the subscription separately.", { duration: 6000 });
        return;
      }
      if (!isNewItemSubscription && cartHasSubscription) {
        toast.error("One-time and subscription products cannot be purchased together. Please complete your current cart first, then purchase this item separately.", { duration: 6000 });
        return;
      }
    }
    
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product_id === product.product_id);
      if (existingItem) {
        return prevCart.map(item =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} added to basket`);
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
  };

  // Update cart item quantity
  const updateQuantity = (productId, delta) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.product_id === productId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  // Calculate cart total
  const cartTotal = cart.reduce((sum, item) => {
    const price = item.price || item.retail_price / 100;
    return sum + (price * item.quantity);
  }, 0);

  // Cart item count
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Buy Now - go directly to checkout
  const handleBuyNow = (product) => {
    navigate(`/product/${product.product_id}`);
  };

  // Open checkout form
  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error("Your basket is empty");
      return;
    }
    
    if (!user) {
      toast.error("Please sign in to checkout");
      navigate("/login", { state: { from: `/store/${merchantId}` } });
      return;
    }
    
    setShowCart(false);
    setShowCheckoutForm(true);
  };

  // Process checkout
  const processCheckout = async () => {
    // Validate form
    if (!checkoutDetails.name || !checkoutDetails.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    
    if (!checkoutDetails.postcode) {
      toast.error("Please enter your postcode");
      return;
    }
    
    // Check for mixed cart (one-time + subscription)
    const hasOneTime = cart.some(item => item.type !== "subscription" && !item.is_subscription);
    const hasSubscription = cart.some(item => item.type === "subscription" || item.is_subscription);
    
    if (hasOneTime && hasSubscription) {
      toast.error("Cannot checkout one-time products and subscriptions together. Please purchase them separately.", { duration: 5000 });
      return;
    }
    
    setCheckoutLoading(true);
    toast.loading("Creating checkout session...", { id: "checkout" });
    
    try {
      const token = localStorage.getItem("token");
      
      // Use guest checkout if not logged in, or authenticated checkout if logged in
      const isGuest = !token;
      const endpoint = isGuest 
        ? `${API}/public/checkout/guest`
        : `${API}/stripe/checkout/cart-session`;
      
      const headers = {
        "Content-Type": "application/json",
      };
      
      // Only add Authorization header if logged in
      if (!isGuest) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          items: cart.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          })),
          merchant_id: merchantId,
          origin_url: window.location.origin,
          customer_details: checkoutDetails,
          wants_delivery: wantsDelivery
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Checkout failed");
      }
      
      const data = await response.json();
      toast.dismiss("checkout");
      
      if (data.url) {
        // Clear cart on successful checkout initiation
        localStorage.removeItem(`cart_${merchantId}`);
        setCart([]);
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      toast.dismiss("checkout");
      toast.error(error.message || "Failed to create checkout session");
      console.error("Checkout error:", error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const scrollToProducts = () => {
    setShowProducts(true);
    setTimeout(() => {
      document.getElementById("products-section")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading store...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !merchant) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Store className="h-20 w-20 text-slate-300 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Store Not Found</h1>
          <p className="text-slate-500 mb-6">
            {error || "This store doesn't exist or is no longer available."}
          </p>
          <Link to="/store">
            <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
              Browse All Stores
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get merchant display info
  const merchantName = merchant.business_name || merchant.name || `${merchant.first_name || ""} ${merchant.last_name || ""}`.trim() || "Store";
  const merchantLogo = merchant.logo_url ? 
    (merchant.logo_url.startsWith('data:') ? merchant.logo_url :
     merchant.logo_url.startsWith('http') ? merchant.logo_url : 
     `${API}${merchant.logo_url}`) 
    : merchant.profile_image;
  const merchantTagline = merchant.tagline || merchant.description || "Welcome to our store";
  const merchantAddress = merchant.address || merchant.business_address;
  const merchantPhone = merchant.phone || merchant.business_phone;
  const merchantEmail = merchant.contact_email || merchant.email;
  const merchantWebsite = merchant.website;
  const merchantCover = merchant.cover_image_url ? 
    (merchant.cover_image_url.startsWith('data:') ? merchant.cover_image_url :
     merchant.cover_image_url.startsWith('http') ? merchant.cover_image_url : 
     `${API}${merchant.cover_image_url}`) 
    : null;
  const merchantCoverPosition = merchant.cover_position ?? 50;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero / Brand Landing Section */}
      <section 
        className="relative min-h-[70vh] flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 overflow-hidden"
        data-testid="merchant-brand-hero"
      >
        {/* Cover Image or Background Pattern */}
        {merchantCover ? (
          <>
            <div className="absolute inset-0">
              <img src={merchantCover} alt="" className="w-full h-full object-cover" style={{ objectPosition: `center ${merchantCoverPosition}%` }} />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          </>
        ) : (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
        )}

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          {/* Logo */}
          {merchantLogo ? (
            <div className="mb-8 animate-fade-in">
              <div className="w-32 h-32 mx-auto rounded-2xl bg-white/10 backdrop-blur-sm p-2 border border-white/20 shadow-2xl">
                <img 
                  src={merchantLogo} 
                  alt={merchantName}
                  className="w-full h-full object-contain rounded-xl"
                  data-testid="merchant-logo"
                />
              </div>
            </div>
          ) : (
            <div className="mb-8 animate-fade-in">
              <div className="w-32 h-32 mx-auto rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <Store className="h-16 w-16 text-white/80" />
              </div>
            </div>
          )}

          {/* Business Name */}
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 animate-fade-in"
            style={{ animationDelay: "100ms" }}
            data-testid="merchant-name"
          >
            {merchantName}
          </h1>

          {/* Tagline */}
          <p 
            className="text-lg sm:text-xl text-white/70 mb-8 max-w-xl mx-auto animate-fade-in"
            style={{ animationDelay: "200ms" }}
            data-testid="merchant-tagline"
          >
            {merchantTagline}
          </p>

          {/* Contact Info Pills */}
          <div 
            className="flex flex-wrap justify-center gap-3 mb-10 animate-fade-in"
            style={{ animationDelay: "300ms" }}
          >
            {merchantAddress && (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm border border-white/10">
                <MapPin className="h-4 w-4" />
                {merchantAddress}
              </span>
            )}
            {merchantPhone && (
              <a 
                href={`tel:${merchantPhone}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm border border-white/10 hover:bg-white/20 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {merchantPhone}
              </a>
            )}
            {merchantWebsite && (
              <a 
                href={merchantWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm border border-white/10 hover:bg-white/20 transition-colors"
              >
                <Globe className="h-4 w-4" />
                Website
              </a>
            )}
          </div>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in"
            style={{ animationDelay: "400ms" }}
          >
            <Button
              onClick={scrollToProducts}
              size="lg"
              className="bg-white text-indigo-900 hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all group"
              data-testid="view-products-btn"
            >
              View Products
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            {user ? (
              <Button
                onClick={() => navigate(user.role === "merchant" ? "/merchant" : "/dashboard")}
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                data-testid="dashboard-link"
              >
                My Dashboard
              </Button>
            ) : (
              <Link to="/login">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                  data-testid="sign-in-link"
                >
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          {/* Product Count Badge */}
          <div 
            className="mt-8 animate-fade-in"
            style={{ animationDelay: "500ms" }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/20 backdrop-blur-sm rounded-full text-indigo-200 text-sm border border-indigo-400/20">
              <ShoppingBag className="h-4 w-4" />
              {products.length} {products.length === 1 ? "Product" : "Products"} Available
            </span>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer"
          onClick={scrollToProducts}
        >
          <ChevronDown className="h-8 w-8 text-white/50" />
        </div>
      </section>

      {/* Products Section */}
      <section id="products-section" className={`transition-all duration-500 ${showProducts ? "opacity-100" : "opacity-70"}`}>
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {merchantLogo ? (
                  <img src={merchantLogo} alt={merchantName} className="h-10 w-10 rounded-lg object-contain" />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Store className="h-5 w-5 text-indigo-600" />
                  </div>
                )}
                <span className="font-semibold text-slate-900 hidden sm:block">{merchantName}</span>
              </div>

              {/* Search Bar */}
              <div className="flex-1 max-w-xl">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-11 bg-slate-100 border-slate-200 focus:bg-white"
                    data-testid="search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Auth Buttons */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <Link to="/store">
                  <Button variant="ghost" className="text-slate-600" data-testid="all-stores-btn">
                    All Stores
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Filters */}
        {categories.length > 0 && (
          <div className="bg-white border-b border-slate-200">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-48" data-testid="category-filter">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span className="text-slate-500 text-sm">
                    {filteredProducts.length} products found
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 rounded-lg transition-colors ${viewMode === "grid" ? "bg-indigo-100 text-indigo-600" : "text-slate-400 hover:bg-slate-100"}`}
                    data-testid="grid-view-btn"
                  >
                    <Grid className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 rounded-lg transition-colors ${viewMode === "list" ? "bg-indigo-100 text-indigo-600" : "text-slate-400 hover:bg-slate-100"}`}
                    data-testid="list-view-btn"
                  >
                    <List className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products Grid */}
        <main className="max-w-7xl mx-auto px-6 py-8">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No products found</h3>
              <p className="text-slate-500 mb-6">
                {searchTerm || selectedCategory !== "all" 
                  ? "Try adjusting your search or filter criteria."
                  : "This store hasn't added any products yet."}
              </p>
              {(searchTerm || selectedCategory !== "all") && (
                <Button
                  onClick={() => { setSearchTerm(""); setSelectedCategory("all"); }}
                  variant="outline"
                  className="rounded-full"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {filteredProducts.map((product, index) => {
                const productImage = product.image_url ? 
                  (product.image_url.startsWith('data:') ? product.image_url :
                   product.image_url.startsWith('http') ? product.image_url : `${API}${product.image_url}`) 
                  : (product.images?.[0] || null);
                return (
                <div
                  key={product.product_id}
                  onClick={() => handleProductClick(product.product_id)}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden cursor-pointer hover:border-indigo-300 hover:shadow-lg transition-all"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`product-card-${index}`}
                >
                  <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                    {productImage ? (
                      <img
                        src={productImage}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="h-12 w-12 text-slate-300" />
                      </div>
                    )}
                    {product.is_subscription && (
                      <span className="absolute top-3 left-3 px-2 py-1 bg-indigo-600 text-white text-xs font-medium rounded-full">
                        Subscription
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-lg text-indigo-600">
                        £{product.price?.toFixed(2) || "0.00"}
                        {product.is_subscription && (
                          <span className="text-sm font-normal text-slate-500">
                            /{product.subscription_interval}
                          </span>
                        )}
                      </span>
                      <Button
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 rounded-full h-9 px-4"
                        onClick={(e) => addToCart(product, e)}
                        data-testid={`add-to-cart-${index}`}
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProducts.map((product, index) => {
                const productImage = product.image_url ? 
                  (product.image_url.startsWith('data:') ? product.image_url :
                   product.image_url.startsWith('http') ? product.image_url : `${API}${product.image_url}`) 
                  : (product.images?.[0] || null);
                return (
                <div
                  key={product.product_id}
                  onClick={() => handleProductClick(product.product_id)}
                  className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4 cursor-pointer hover:border-indigo-300 transition-colors"
                  data-testid={`product-list-${index}`}
                >
                  <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                    {productImage ? (
                      <img
                        src={productImage}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="h-8 w-8 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">
                          {product.name}
                          {product.is_subscription && (
                            <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs font-medium rounded-full">
                              Subscription
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-2">
                          {product.description}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 flex flex-col items-end gap-2">
                        <span className="font-bold text-lg text-indigo-600">
                          £{product.price?.toFixed(2) || "0.00"}
                        </span>
                        {product.is_subscription && (
                          <span className="text-sm text-slate-500">
                            per {product.subscription_interval}
                          </span>
                        )}
                        <Button
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 rounded-full"
                          onClick={(e) => addToCart(product, e)}
                        >
                          <ShoppingCart className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </main>
      </section>

      {/* Floating Cart Button */}
      {cartItemCount > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-indigo-600 text-white rounded-full p-4 shadow-lg hover:bg-indigo-700 transition-all hover:scale-105 z-50"
          data-testid="floating-cart-btn"
        >
          <ShoppingCart className="h-6 w-6" />
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
            {cartItemCount}
          </span>
        </button>
      )}

      {/* Cart Modal */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="cart-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {/* Merchant Logo */}
              {merchant?.logo_url && (
                <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-100 flex-shrink-0">
                  <img 
                    src={merchant.logo_url.startsWith('data:') ? merchant.logo_url : 
                         merchant.logo_url.startsWith('http') ? merchant.logo_url : `${API}${merchant.logo_url}`}
                    alt={merchant.business_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <span className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-indigo-600" />
                  Your Basket ({cartItemCount} items)
                </span>
                {merchant?.business_name && (
                  <p className="text-xs text-slate-500 font-normal">{merchant.business_name}</p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Your basket is empty</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCart(false)}
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item, idx) => {
                const itemImage = item.image_url ? 
                  (item.image_url.startsWith('data:') ? item.image_url :
                   item.image_url.startsWith('http') ? item.image_url : `${API}${item.image_url}`) 
                  : (item.images?.[0] || null);
                const itemPrice = item.price || item.retail_price / 100;
                const lineTotal = itemPrice * item.quantity;
                return (
                  <div key={item.product_id} className="flex gap-3 p-3 bg-slate-50 rounded-xl" data-testid={`cart-item-${idx}`}>
                    <div className="w-16 h-16 bg-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                      {itemImage ? (
                        <img src={itemImage} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">{item.name}</h4>
                      <div className="flex items-center justify-between">
                        <p className="text-slate-500 text-sm">£{itemPrice.toFixed(2)} each</p>
                        <p className="text-indigo-600 font-semibold" data-testid={`line-total-${idx}`}>£{lineTotal.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={() => updateQuantity(item.product_id, -1)}
                          className="w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center"
                          data-testid={`decrease-qty-${idx}`}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, 1)}
                          className="w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center"
                          data-testid={`increase-qty-${idx}`}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          className="ml-auto text-red-500 hover:text-red-600"
                          data-testid={`remove-item-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Delivery Option - only show if any product offers delivery */}
              {cart.some(item => item.offers_delivery) && (
                <div className="bg-indigo-50 rounded-lg p-4 mb-4" data-testid="delivery-option">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={wantsDelivery}
                      onChange={(e) => setWantsDelivery(e.target.checked)}
                      className="w-5 h-5 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex items-center gap-2">
                      <Truck className="h-5 w-5 text-indigo-600" />
                      <div>
                        <span className="font-medium text-slate-900">Deliver to my address</span>
                        <p className="text-xs text-slate-500">Merchant handles delivery manually</p>
                      </div>
                    </div>
                  </label>
                </div>
              )}
              
              {/* Cart Total */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-slate-700">Subtotal ({cartItemCount} items)</span>
                  <span className="text-lg font-semibold text-slate-900">£{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mb-4 text-sm text-slate-500">
                  <span>Shipping</span>
                  <span>Free</span>
                </div>
                <div className="flex justify-between items-center mb-4 pb-4 border-b">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="text-xl font-bold text-indigo-600" data-testid="cart-total">£{cartTotal.toFixed(2)}</span>
                </div>
                <div className="space-y-2">
                  <Button
                    onClick={handleCheckout}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full h-12"
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowCart(false)}
                      className="flex-1 rounded-full"
                    >
                      Continue Shopping
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCart([]);
                        localStorage.removeItem(`cart_${merchantId}`);
                        toast.success("Cart cleared");
                      }}
                      className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="clear-cart-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Detail Modal */}
      <Dialog open={showProductDetail} onOpenChange={setShowProductDetail}>
        <DialogContent className="max-w-lg" data-testid="product-detail-modal">
          {selectedProduct && (
            <>
              <div className="aspect-video bg-slate-100 rounded-xl overflow-hidden mb-4">
                {(() => {
                  const prodImage = selectedProduct.image_url ? 
                    (selectedProduct.image_url.startsWith('data:') ? selectedProduct.image_url :
                     selectedProduct.image_url.startsWith('http') ? selectedProduct.image_url : `${API}${selectedProduct.image_url}`) 
                    : (selectedProduct.images?.[0] || null);
                  return prodImage ? (
                    <img src={prodImage} alt={selectedProduct.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="h-16 w-16 text-slate-300" />
                    </div>
                  );
                })()}
              </div>
              
              <DialogHeader>
                <DialogTitle className="text-xl">{selectedProduct.name}</DialogTitle>
              </DialogHeader>
              
              <p className="text-slate-600 mt-2">{selectedProduct.description}</p>
              
              <div className="flex items-center gap-2 mt-4">
                <span className="text-2xl font-bold text-indigo-600">
                  £{(selectedProduct.price || 0).toFixed(2)}
                </span>
                {selectedProduct.is_subscription && (
                  <span className="text-slate-500">/{selectedProduct.subscription_interval}</span>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => {
                    addToCart(selectedProduct);
                    setShowProductDetail(false);
                  }}
                  data-testid="add-to-basket-detail-btn"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Add to Basket
                </Button>
                <Button
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full"
                  onClick={() => {
                    handleBuyNow(selectedProduct);
                    setShowProductDetail(false);
                  }}
                  data-testid="buy-now-detail-btn"
                >
                  Buy Now
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Form Modal */}
      <Dialog open={showCheckoutForm} onOpenChange={setShowCheckoutForm}>
        <DialogContent className="max-w-md" data-testid="checkout-form-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              Checkout
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Order Summary */}
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-600 mb-2">{cart.length} item(s) • Total</p>
              <p className="text-2xl font-bold text-indigo-600">£{cartTotal.toFixed(2)}</p>
            </div>
            
            {/* Customer Details Form */}
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Full Name *</label>
                <Input
                  value={checkoutDetails.name}
                  onChange={(e) => setCheckoutDetails({...checkoutDetails, name: e.target.value})}
                  placeholder="Enter your full name"
                  data-testid="checkout-name"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Email Address *</label>
                <Input
                  type="email"
                  value={checkoutDetails.email}
                  onChange={(e) => setCheckoutDetails({...checkoutDetails, email: e.target.value})}
                  placeholder="Enter your email"
                  data-testid="checkout-email"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Phone Number</label>
                <Input
                  type="tel"
                  value={checkoutDetails.phone}
                  onChange={(e) => setCheckoutDetails({...checkoutDetails, phone: e.target.value})}
                  placeholder="Enter your phone number"
                  data-testid="checkout-phone"
                />
              </div>
              
              <div className="border-t pt-3 mt-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Shipping Address (UK only)</p>
                
                <div className="space-y-2">
                  <Input
                    value={checkoutDetails.address}
                    onChange={(e) => setCheckoutDetails({...checkoutDetails, address: e.target.value})}
                    placeholder="Address line"
                    data-testid="checkout-address"
                  />
                  
                  <div className="flex gap-2">
                    <Input
                      value={checkoutDetails.city}
                      onChange={(e) => setCheckoutDetails({...checkoutDetails, city: e.target.value})}
                      placeholder="City"
                      className="flex-1"
                      data-testid="checkout-city"
                    />
                    <Input
                      value={checkoutDetails.postcode}
                      onChange={(e) => setCheckoutDetails({...checkoutDetails, postcode: e.target.value.toUpperCase()})}
                      placeholder="Postcode *"
                      className="w-28"
                      data-testid="checkout-postcode"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Secure Payment Note */}
            <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
              <Lock className="h-4 w-4" />
              <span>Secure payment powered by Stripe</span>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              onClick={() => setShowCheckoutForm(false)}
              className="flex-1"
            >
              Back to Cart
            </Button>
            <Button 
              onClick={processCheckout}
              disabled={checkoutLoading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              data-testid="place-order-btn"
            >
              {checkoutLoading ? (
                <>Processing...</>
              ) : (
                <>Place Order</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400">
            © {new Date().getFullYear()} {merchantName}. Powered by{" "}
            <Link to="/" className="text-indigo-400 hover:text-indigo-300">
              Veyronix
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MerchantStorePage;
